import { Stack } from '@mui/material';
import Button from '@mui/material/Button';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../../api/http';
import { getFutureAndGeneratedInvoicesQueryKey } from '../../../api/InvoiceApi';
import PolicyApi, { getUsePolicyQueryKey } from '../../../api/PolicyApi';
import { getInvoicesForPolicyholderQueryKey } from '../../../api/PolicyholderApi';
import { AuthData } from '../../../context/auth';
import { ErrorData } from '../../../context/ErrorProvider';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import DropdownButton from '../../DropdownButton';
import { getDefaultPolicyValues } from '../../fastForm/formBuilders';
import { successToast } from '../../Toast';
import ActionConfirmationModal from '../ActionConfirmationModal';

interface Props {
	policyData: PolicyData;
	authData: AuthData;
	errorData: ErrorData;
}

const QuotedQuoteActions = (props: Props) => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { reset } = useFormContext();
	const { authData, policyData, errorData } = props;
	const { hasPermission } = authData;
	const { selectedQuote, quotes, setLoading, setQuoteUpdateObj, policy } =
		props.policyData;

	const refetchData = () => {
		const policyQueryKey = getUsePolicyQueryKey(
			policy.locator,
			authData?.auth?.authorizationToken,
		);
		queryClient.invalidateQueries(policyQueryKey);
		queryClient.invalidateQueries(
			getInvoicesForPolicyholderQueryKey(
				authData?.auth?.authorizationToken,
				policy.policyholderLocator,
			),
		);
		queryClient.invalidateQueries(
			getFutureAndGeneratedInvoicesQueryKey(
				policy.locator,
				authData?.auth?.authorizationToken,
			),
		);
	};

	const onAccept = async () => {
		const { authData, policyData } = props;
		setLoading(true);
		try {
			await PolicyApi.acceptQuote(
				authData.auth.authorizationToken,
				selectedQuote.locator,
			);
			await policyData.loadData(policy.locator);
			refetchData();
			successToast('Successfully accepted quote');
		} catch (error) {
			errorData.handleErrorResponse(error, 'Failed to accept quote');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}
		setLoading(false);
	};

	const createNewQuote = async () => {
		setLoading(true);
		const {
			authData: {
				auth: { authorizationToken },
			},
			policyData,
		} = props;
		try {
			const newQuote = await PolicyApi.createNewQuote(
				authorizationToken,
				selectedQuote.locator,
			);
			setQuoteUpdateObj({}); // reset update obj
			await PolicyApi.selectQuote(authorizationToken, newQuote.data.locator);
			const updatedPolicyData = await policyData.loadData(policy.locator);
			const defaultPolicyValues = getDefaultPolicyValues(
				updatedPolicyData.fields,
				props.authData,
			);
			reset(defaultPolicyValues);
			successToast('Successfully created new quote');
			navigate(`/policy/${policy.locator}/quotes/${newQuote.data.locator}`);
			await policyData.loadData(policy.locator);
		} catch (error) {
			errorData.handleErrorResponse(error, 'Failed to create new quote');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}
		await policyData.loadData(policy.locator);
		setLoading(false);
	};

	const discardQuote = async () => {
		setLoading(true);
		const {
			authData: {
				auth: { authorizationToken },
			},
		} = props;
		try {
			await PolicyApi.discardQuote(authorizationToken, selectedQuote.locator);
			successToast('Successfully discarded quote');
			const newSelectedQuote = quotes.filter(
				(quote) => quote.locator !== selectedQuote.locator,
			)[0];
			await PolicyApi.selectQuote(authorizationToken, newSelectedQuote.locator);
			const updatedPolicyData = await policyData.loadData(policy.locator);
			const defaultPolicyValues = getDefaultPolicyValues(
				updatedPolicyData.fields,
				props.authData,
			);
			reset(defaultPolicyValues);
			navigate(`/policy/${policy.locator}/quotes/${newSelectedQuote.locator}`);
			await policyData.loadData(policy.locator);
			setLoading(false);
			return;
		} catch (error) {
			errorData.handleErrorResponse(error, 'Failed to discard quote');
			if (await handleUnauthorizedResponse(error, authData)) {
				setLoading(false);
				return;
			}
		}
	};

	const discardPolicy = async () => {
		setLoading(true);
		const {
			authData: {
				auth: { authorizationToken },
			},
			policyData,
		} = props;
		try {
			await PolicyApi.discardPolicy(authorizationToken, policy.locator);
			successToast('Successfully discarded policy');
		} catch (error) {
			errorData.handleErrorResponse(error, 'Failed to discard policy');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}
		policyData.clear();
		navigate(`/policyholder/${policyData.policy.policyholderLocator}`);
		setLoading(false);
	};

	const onIssueFromQuote = async () => {
		setLoading(true);
		const { authData, policyData } = props;
		try {
			await PolicyApi.acceptQuote(
				authData.auth.authorizationToken,
				selectedQuote.locator,
			);
			await PolicyApi.issuePolicy(
				authData.auth.authorizationToken,
				policy.locator,
			);
			await policyData.loadData(policy.locator);
			setQuoteUpdateObj({});
			successToast('Successfully issued policy');
		} catch (error) {
			errorData.handleErrorResponse(error, 'Failed to issue policy');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}
		setLoading(false);
	};

	let buttons;
	const hiddenOptions = [];

	hasPermission('quote.accept') &&
		hasPermission('policy.issue') &&
		hiddenOptions.push({
			label: 'Issue Policy',
			callback: null,
			component: (
				<ActionConfirmationModal
					dropdownItem
					label='Issue Policy'
					message='Are you sure you want to issue this quote?'
					confirmText='Issue'
					onConfirm={onIssueFromQuote}
				/>
			),
		});
	hasPermission('quote.create') &&
		hiddenOptions.push({ label: 'Create New Quote', callback: createNewQuote });

	if (quotes.length > 1) {
		hasPermission('quote.discard') &&
			hasPermission('quote.quote') &&
			hiddenOptions.push({
				label: 'Discard Quote',
				callback: null,
				component: (
					<ActionConfirmationModal
						dropdownItem
						label='Discard Quote'
						message='Are you sure you want to discard this quote?'
						confirmText='Discard Quote'
						onConfirm={discardQuote}
					/>
				),
			});
	} else {
		hasPermission('policy.discard') &&
			hiddenOptions.push({
				label: 'Discard Policy',
				callback: null,
				component: (
					<ActionConfirmationModal
						dropdownItem
						label='Discard Policy'
						message='Are you sure you want to discard this policy?'
						confirmText='Discard Policy'
						onConfirm={discardPolicy}
					/>
				),
			});
	}

	buttons = (
		<Stack direction='row' spacing={1}>
			{hasPermission('quote.accept') && (
				<Button
					key={'accept'}
					color='primary'
					onClick={onAccept}
					variant='contained'
				>
					Accept Policy
				</Button>
			)}
			<DropdownButton actions={hiddenOptions} />
		</Stack>
	);

	return buttons;
};

export default withPolicyContext<Props>(QuotedQuoteActions);
